import { formatAddress } from '@/helpers/formatAddress';
import { getLatestReportByProvider, getGlideRows, getDtapiRows } from '@/helpers/propertyDetailsHelper';
import { useGetPropertyReportsQuery } from '@/services/propertyReports';
import { Box, CheckboxInput, FormikSelectInput, TextInput } from '@palmetto/palmetto-components';
import { Field, useFormikContext } from 'formik';

import { propertyTypeLabelsMap, PropertyType, parcelProviderLabelsMap, ParcelProviderType, Address } from 'types';

import FormSection from '../Forms/FormSection';

interface PropertyDetailsProps {
  accountId: string;
  isFinCoUser: boolean;
  propertyAddress: Address;
}

interface SolarInstallPackagePropertyTypeValues {
  propertyType?: { label: string; value: string };
  isSecondSystem?: boolean;
}

interface ProviderPanelProps {
  title: string;
  rows: { label: string; value: React.ReactNode }[] | null;
}

const RowItem = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <Box direction="row" gap="md">
    <strong>{label}:</strong> {children}
  </Box>
);

const ProviderPanel = ({ title, rows }: ProviderPanelProps) => (
  <Box gap="md" width="45">
    <Box direction="row" alignItems="center">
      <strong>{title}</strong>
    </Box>
    {rows?.length ? (
      <Box direction="column" gap="md">
        {rows.map((row) => (
          <RowItem key={row.label} label={row.label}>
            {row.value}
          </RowItem>
        ))}
      </Box>
    ) : (
      <Box>Property Report not available</Box>
    )}
  </Box>
);

export const PropertyDetails = ({ isFinCoUser, accountId, propertyAddress }: PropertyDetailsProps) => {
  const { values, setFieldValue } = useFormikContext<SolarInstallPackagePropertyTypeValues>();
  const { isSubmitting } = useFormikContext<SolarInstallPackagePropertyTypeValues>();
  const { data: reports = [] } = useGetPropertyReportsQuery({
    accountId,
    includeReportDetails: true,
  });

  const propertyTypeOptions = Object.keys(PropertyType).map((key) => ({
    label: propertyTypeLabelsMap[key as PropertyType],
    value: key,
  }));
  const glideReport = getLatestReportByProvider(reports, ParcelProviderType.glide);
  const dtapiReport = getLatestReportByProvider(reports, ParcelProviderType.dtapi);
  const glideRows = glideReport ? getGlideRows(glideReport) : null;
  const dtapiRows = dtapiReport ? getDtapiRows(dtapiReport) : null;
  const accountPropertyAddress = formatAddress(propertyAddress);

  return (
    <FormSection title="Palmetto Verified Property">
      <Box gap="md" direction={{ base: 'row', tablet: 'column' }} wrap>
        <Box width="50">
          {isFinCoUser ? (
            <Field
              label="Palmetto Verified Property Type"
              name="propertyType"
              id="propertyType"
              options={propertyTypeOptions}
              component={FormikSelectInput}
              isDisabled={isSubmitting}
              isRequired
            />
          ) : (
            <TextInput
              type="text"
              label="Palmetto Verified Property Type"
              name="propertyType"
              id="propertyType"
              value={values.propertyType?.label ?? ''}
              onChange={() => {}}
              isDisabled
            />
          )}
        </Box>
        <CheckboxInput
          id="secondSystemAttestationCheckbox"
          label="Is an existing solar system present?"
          onChange={(event) => setFieldValue('isSecondSystem', event.target.checked)}
          isChecked={values.isSecondSystem ?? false}
          isDisabled={isSubmitting || !isFinCoUser}
        />
        {isFinCoUser && (
          <>
            <Box as="h2" fontWeight="medium" padding={'md 0 0 0'}>
              Property Address
            </Box>
            <Box as="p" color="body-secondary">
              {accountPropertyAddress}
            </Box>
            <Box as="h2" fontWeight="medium" padding={'md 0 0 0'}>
              Property Reports
            </Box>
            <Box gap="lg" direction={{ base: 'column', tablet: 'row' }} justifyContent="stretch">
              <ProviderPanel title={parcelProviderLabelsMap[ParcelProviderType.glide]} rows={glideRows} />
              <Box padding={{ base: 'lg 0', tablet: '0 lg' }}>
                <ProviderPanel title={parcelProviderLabelsMap[ParcelProviderType.dtapi]} rows={dtapiRows} />
              </Box>
            </Box>
          </>
        )}
      </Box>
    </FormSection>
  );
};
